/**
 * Move — daily micro-workout dashboard.
 *
 * Duolingo-style experience: open → press INIZIA → follow → done.
 * All workouts are generated server-side from the exercise library
 * (no static workout lists). See backend routers/move.py.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { getMoveHome, generateMoveWorkout } from '../lib/api';
import { localized } from '../lib/moveMedia';
import {
  ChevronLeft, Play, Flame, Clock, Zap, Trophy, Loader2,
  Dumbbell, StretchHorizontal, Footprints, Wind, Timer,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  mobility: Wind,
  core: Zap,
  legs: Footprints,
  stretching: StretchHorizontal,
};

export default function Move() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { t, language } = useTranslation();

  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null); // key of card being started

  const load = useCallback(async () => {
    try {
      const res = await getMoveHome();
      setHome(res.data);
    } catch (_) {
      toast.error(t('move.error', 'Errore di caricamento. Riprova.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Feature guard: if the admin disabled the section, non-admin users
  // are redirected to the Dashboard (deep links / old bookmarks).
  useEffect(() => {
    if (user?.is_admin) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/public`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!cancelled && cfg && cfg.move_enabled === false) {
          navigate('/app/dashboard', { replace: true });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, navigate]);

  // Reset scroll on open — the router-level scroll container keeps the
  // previous page's position, which left this page pre-scrolled past
  // the hero card. The real scroller is the App.js wrapper div, not
  // the window (matters on native iOS/Android).
  useEffect(() => {
    const mainContainer = document.querySelector('.h-screen.overflow-y-auto');
    if (mainContainer) mainContainer.scrollTo({ top: 0, behavior: 'instant' });
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const startWorkout = (workout) => {
    navigate('/app/move/player', { state: { workout } });
  };

  const startGenerated = async (key, params) => {
    setStarting(key);
    try {
      const res = await generateMoveWorkout(params);
      startWorkout(res.data);
    } catch (_) {
      toast.error(t('move.error', 'Errore di caricamento. Riprova.'));
    } finally {
      setStarting(null);
    }
  };

  // Avvio automatico dal Coach (Action PROPOSE_MOVE_WORKOUT confermata)
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const mins = parseInt(qs.get('coach_minutes'), 10);
    if (![5, 8, 12].includes(mins)) return;
    const cat = qs.get('coach_category');
    const params = { duration_minutes: mins, ...(cat ? { category: cat } : {}) };
    window.history.replaceState({}, '', window.location.pathname);
    startGenerated('coach', params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const today = home?.today;
  const stats = home?.stats || {};

  const statCards = [
    { key: 'workouts', icon: Trophy, value: stats.workouts_completed ?? 0, label: t('move.stats.workouts', 'Workout completati') },
    { key: 'minutes', icon: Clock, value: stats.total_minutes ?? 0, label: t('move.stats.minutes', 'Minuti totali') },
    { key: 'streak', icon: Zap, value: stats.streak ?? 0, label: t('move.stats.streak', 'Serie consecutiva') },
    { key: 'calories', icon: Flame, value: stats.calories ?? 0, label: t('move.stats.calories', 'Calorie stimate') },
  ];

  const recommended = [
    { key: 'd5', label: t('move.durations.5', '5 minuti'), icon: Timer, params: { duration_minutes: 5 } },
    { key: 'd8', label: t('move.durations.8', '8 minuti'), icon: Timer, params: { duration_minutes: 8 } },
    { key: 'd12', label: t('move.durations.12', '12 minuti'), icon: Timer, params: { duration_minutes: 12 } },
    { key: 'mobility', label: t('move.categories.mobility', 'Mobilità'), icon: CATEGORY_ICONS.mobility, params: { category: 'mobility' } },
    { key: 'core', label: t('move.categories.core', 'Core'), icon: CATEGORY_ICONS.core, params: { category: 'core' } },
    { key: 'legs', label: t('move.categories.legs', 'Gambe'), icon: CATEGORY_ICONS.legs, params: { category: 'legs' } },
    { key: 'stretching', label: t('move.categories.stretching', 'Stretching'), icon: CATEGORY_ICONS.stretching, params: { category: 'stretching' } },
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`} data-testid="move-page">
      {/* Header */}
      <div className={`fixed-header ${isDark ? 'bg-[#0D1424]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => navigate('/app/dashboard')}
              className={`p-1.5 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              data-testid="move-back-btn"
            >
              <ChevronLeft className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-900'}`} />
            </button>
            <div className="flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-[#E8334A]" />
              <h1 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('move.title', 'Move')}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="content-below-header pb-24 px-4 max-w-md mx-auto">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E8334A]" />
          </div>
        ) : (
          <>
            {/* Hero — Move di oggi */}
            {today && (
              <div
                className="rounded-3xl p-6 mb-6 relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)' }}
                data-testid="move-hero-card"
              >
                <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
                <div className="absolute -right-2 top-16 w-20 h-20 rounded-full bg-white/10" />
                <p className="text-white/90 text-sm font-semibold uppercase tracking-wide mb-1">
                  {t('move.todayWorkout', 'Move di oggi')}
                </p>
                <div className="flex items-end gap-2 mb-1">
                  <span className="font-display text-white text-4xl leading-none">
                    {today.estimated_minutes}
                  </span>
                  <span className="text-white/90 text-lg font-medium mb-0.5">
                    {t('move.minutes', 'minuti')}
                  </span>
                </div>
                <p className="text-white/85 text-sm mb-4">
                  {today.exercises_count} {t('move.exercises', 'esercizi')} · {t('move.level', 'Livello')}{' '}
                  {t(`move.levels.${today.level}`, 'Base')}
                </p>
                <button
                  onClick={() => startWorkout(today)}
                  className="w-full py-4 rounded-2xl bg-white text-[#E8334A] font-display text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg"
                  data-testid="move-start-today-btn"
                >
                  <Play className="w-5 h-5 fill-current" />
                  {t('move.start', 'INIZIA')}
                </button>
              </div>
            )}

            {/* Stats */}
            <h2 className={`font-display text-lg mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('move.stats.title', 'Le tue statistiche')}
            </h2>
            <div className="grid grid-cols-2 gap-3 mb-6" data-testid="move-stats-grid">
              {statCards.map(({ key, icon: Icon, value, label }) => (
                <div
                  key={key}
                  className={`rounded-2xl p-4 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-100'}`}
                  data-testid={`move-stat-${key}`}
                >
                  <Icon className="w-5 h-5 text-[#E8334A] mb-2" />
                  <p className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
                  <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{label}</p>
                </div>
              ))}
            </div>

            {/* Recommended */}
            <h2 className={`font-display text-lg mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('move.recommended', 'Workout consigliati')}
            </h2>
            <div className="grid grid-cols-2 gap-3" data-testid="move-recommended-grid">
              {recommended.map(({ key, label, icon: Icon, params }) => (
                <button
                  key={key}
                  onClick={() => startGenerated(key, params)}
                  disabled={!!starting}
                  className={`rounded-2xl p-4 text-left flex items-center gap-3 active:scale-[0.98] transition-transform ${
                    isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-100'
                  } ${starting && starting !== key ? 'opacity-60' : ''}`}
                  data-testid={`move-recommended-${key}`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgba(232, 51, 74, 0.12)' }}>
                    {starting === key
                      ? <Loader2 className="w-5 h-5 animate-spin text-[#E8334A]" />
                      : <Icon className="w-5 h-5 text-[#E8334A]" />}
                  </div>
                  <span className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</span>
                </button>
              ))}
            </div>

            {/* Published admin workouts (data-driven, optional) */}
            {home?.published_workouts?.length > 0 && (
              <div className="mt-6">
                <h2 className={`font-display text-lg mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('move.moreWorkouts', 'Altri workout')}
                </h2>
                <div className="space-y-3">
                  {home.published_workouts.map((w) => (
                    <button
                      key={w.workout_id}
                      onClick={() => startGenerated(`tpl_${w.workout_id}`, { workout_id: w.workout_id })}
                      disabled={!!starting}
                      className={`w-full rounded-2xl p-4 text-left flex items-center gap-3 active:scale-[0.98] transition-transform ${
                        isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-100'
                      }`}
                      data-testid={`move-published-${w.workout_id}`}
                    >
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: 'rgba(232, 51, 74, 0.12)' }}>
                        {starting === `tpl_${w.workout_id}`
                          ? <Loader2 className="w-5 h-5 animate-spin text-[#E8334A]" />
                          : <Dumbbell className="w-5 h-5 text-[#E8334A]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {localized(w.name, language)}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                          {(w.items || []).length} {t('move.exercises', 'esercizi')}
                          {w.estimated_minutes ? ` · ${w.estimated_minutes} ${t('move.minutes', 'minuti')}` : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
